import React, { useRef, useState, useEffect, useCallback } from "react";

import styles from "styles.module.css";

import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addWeeks } from "date-fns";
import OverLayLoading, { Loading } from "ui/Loading";
import { useTranslation } from "react-i18next";
import Filter from "./Filter";
import "./style.css";
import { dailySalesFreshColumnItem } from "./utils/GridColumns";
import { getFreshSaleApi } from "api/sales";
import { FilterOption } from "interface";
import GroupGrid from "ui/grid/GroupGrid";
import GroupFooterCell from "./cells/GroupFooterCell";
import { usePageAccess } from "hooks/usePageAccess";
import { FreshSale } from "interface/sales";
import FreshSalesMobileList from "./FreshSalesMobileList";

const pageSize = 60;

const FreshSalesPage = () => {
	const { t } = useTranslation();
	const { loading } = usePageAccess();

	const overlayRef = useRef<HTMLDivElement>(null);

	const gridRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<FreshSale[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const [filterOptions, setFilterOptions] = useState<FilterOption>({
		take: pageSize,
		skip: 0,
		fromDate: addWeeks(new Date(), -1),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getFreshSaleApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as FreshSale[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	if (loading) {
		return <Loading show={true} />;
	}

	return (
		<div className={`${styles.PageContainer} fresh-sales-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("FreshSales")}</legend>
			</div>
			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<FreshSalesMobileList
						items={mobileData}
						loading={mobileLoading}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GroupGrid
						gridColumns={dailySalesFreshColumnItem()}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getFreshSaleApi}
						className={"daily-fresh-sales-group-grid"}
						excelProps={{
							fileName: `fresh_sales_${new Date().toISOString()}.xlsx`,
						}}
						cells={{
							groupFooter: GroupFooterCell,
							// groupHeader: GroupHeaderCell,
						}}
						groups={[
							{
								field: "group1",
								aggregates: [{ field: "netLiveWeightKg", aggregate: "sum" }],
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default FreshSalesPage;
